import React, { CSSProperties, ReactNode, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Resizable } from 'react-resizable';
import styles from 'src/app/components/DraggableModalComponent/DraggableModal.module.scss';
import { useAppSelector } from 'src/app/redux/hook';
import { PureCancelIcon } from 'src/icons';
import tinycolor from 'tinycolor2';

type ResizeOptionsType = {
  minConstraints?: [number, number];
  maxConstraints?: [number, number];
};
interface IDraggableModalProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
  title: string;
  contentClassName?: string;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  onClose: () => void;
  resizeOpts?: ResizeOptionsType;

  disableHeader?: boolean;

  disableResize?: boolean;
  contentDraggable?: boolean;
  onResize?: (width: number, height: number) => void;

  size?: {
    width?: number;
    height?: number;
  };

  footer?: ReactNode;
}

// This component is used to show draggable and resizable modal
const DraggableModal: React.FC<IDraggableModalProps> = ({
  children,
  title,
  className,
  id,
  contentClassName,
  style,
  onClose,
  resizeOpts,
  disableHeader,
  disableResize,
  contentDraggable,
  size,
  containerStyle,
  footer,
  onResize,
}) => {
  const { theme } = useAppSelector((state) => state.configContent);
  const draggleRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<any>({
    left: null,
    right: null,
    top: null,
    bottom: null,
  });

  const [width, setWidth] = useState(size?.width);
  const [height, setHeight] = useState(size?.height);

  const handleResize = (event: any, { size: newSize }: { size: any }) => {
    setWidth(newSize.width);
    setHeight(newSize.height);
    if (onResize) onResize(newSize.width, newSize.height);
  };

  /*
  On drag start set bounds to the dragged coordinates
  */
  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const content = (
    <div
      id={id}
      ref={draggleRef}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        ...containerStyle,
      }}
    >
      {/* Do not show header if disable header prop is true */}
      {!disableHeader ? (
        <div
          className={styles.header}
          style={{
            background: theme.colors.quoteBgc,
            color: tinycolor(theme.colors.quoteBgc).isDark() ? '#fff' : '#000',
          }}
        >
          <p className={styles.title}>{title}</p>

          <PureCancelIcon
            color={tinycolor(theme.colors.quoteBgc).isDark() ? '#fff' : '#000'}
            onClick={() => {
              onClose();
            }}
            style={{ height: '18px', width: '18px' }}
            className="cancel-drag"
          />
        </div>
      ) : null}

      <div
        // Check contentDraggable prop to decide content is draggable or disable it with "cancel-drag" class name
        className={`${contentClassName || styles.dragContent} ${contentDraggable ? undefined : 'cancel-drag'}`}
        style={{
          padding: '1rem',
          ...style,
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : '100%',
          background: 'var(--header_bg)',
        }}
      >
        <div style={{ height: '100%', width: '100%' }}>{children}</div>
      </div>
      {footer ? (
        <div className="cancel-drag" style={{ background: 'var(--header_bg)' }}>
          {footer}
          <div style={{ width: '100%', background: theme.colors.quoteBgc, height: '5px' }} />
        </div>
      ) : null}
    </div>
  );

  return (
    <Draggable
      cancel=".cancel-drag, .react-resizable-handle"
      onStart={(event, uiData) => onStart(event, uiData)}
      bounds={bounds}
      positionOffset={{
        x: containerStyle?.left ? `-${containerStyle.left}` : '-50%',
        y: containerStyle?.top ? `-${containerStyle.top}` : '-50%',
      }}
      defaultClassName={className || styles.draggableModal}
      nodeRef={draggleRef}
    >
      {!disableResize ? (
        <Resizable
          width={width || 500}
          height={height || 300}
          onResize={handleResize}
          minConstraints={resizeOpts?.minConstraints ? resizeOpts.minConstraints : [500, 300]}
          maxConstraints={resizeOpts?.maxConstraints ? resizeOpts.maxConstraints : [800, 600]}
        >
          {content}
        </Resizable>
      ) : (
        content
      )}
    </Draggable>
  );
};

export default DraggableModal;
